/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * Bulk Focus Ask renderer. The workspace `create-asks` skill emits a single
 * `MessageAction` whose `data.asks` array carries every ask the planner wants
 * to create. We expand that array into one row per ask and render the whole
 * batch as a single card so the user can review, edit, drop individual asks,
 * or submit the remaining set in one shot.
 */
import React, { Fragment, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter } from '../../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../../common/utils/buildToolFromAction';
import { FocusAskCreateModal } from '../ask-create-modal';
import { extractAsks } from '../extract-asks';
import { MAX_ASK_NAME_LENGTH } from '../fields/name';
import { FocusAskActionCardWrapper } from '../FocusAskActionCardWrapper';
import { mergeFocusAskFormIntoPayload } from '../merge-focus-ask-form-into-payload';
import messages from '../messages';
import type { FocusAskFormData } from '../types';

import { BulkFocusAskRow, type BulkFocusAskRowStatus } from './BulkFocusAskRow';
import { useBulkFocusAskState } from './useBulkFocusAskState';

type BulkFocusAskCreateActionProps = Omit<
	ActionRendererFor<typeof ActionKeys.FocusCreateAsk>,
	'orgId'
> & {
	orgId: string;
};

const styles = cssMap({
	footerContent: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
	},
});

const MAX_ASKS_PER_REQUEST = 25;
const MAX_CREATED_ASK_LINK_EXTRACTION_DEPTH = 5;
const CREATED_ASK_LINK_KEYS = ['link', 'url', 'href', 'webUrl'] as const;
const CREATED_ASK_COLLECTION_KEYS = [
	'asks',
	'items',
	'results',
	'data',
	'message',
	'message_metadata',
	'actionOutput',
	'urls',
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isCreateAskErrorResponse = (value: unknown): boolean =>
	isRecord(value) &&
	((typeof value.statusCode === 'number' && value.statusCode >= 400) ||
		(typeof value.errorMessage === 'string' && value.errorMessage.length > 0) ||
		(Array.isArray(value.errors) && value.errors.length > 0));

const extractCreatedAskLinkFromRecord = (value: unknown, depth = 0): string | undefined => {
	if (depth > MAX_CREATED_ASK_LINK_EXTRACTION_DEPTH || !isRecord(value)) {
		return undefined;
	}

	for (const key of CREATED_ASK_LINK_KEYS) {
		const candidate: unknown = value[key];
		if (typeof candidate === 'string' && candidate.trim()) {
			return candidate;
		}

		const nestedLink = extractCreatedAskLinkFromRecord(candidate, depth + 1);
		if (nestedLink) {
			return nestedLink;
		}
	}

	return undefined;
};

const extractCreatedAskLinks = (value: unknown, depth = 0): string[] => {
	if (depth > MAX_CREATED_ASK_LINK_EXTRACTION_DEPTH) {
		return [];
	}

	if (Array.isArray(value)) {
		return value.flatMap((entry) => {
			if (typeof entry === 'string') {
				const trimmed = entry.trim();
				return trimmed ? [trimmed] : [];
			}
			return extractCreatedAskLinks(entry, depth + 1);
		});
	}

	if (!isRecord(value)) {
		return [];
	}

	const directLink = extractCreatedAskLinkFromRecord(value, depth);
	if (directLink) {
		return [directLink];
	}

	for (const key of CREATED_ASK_COLLECTION_KEYS) {
		const candidate: unknown = value[key];
		if (candidate && candidate !== value) {
			const nestedLinks = extractCreatedAskLinks(candidate, depth + 1);
			if (nestedLinks.length > 0) {
				return nestedLinks;
			}
		}
	}

	return [];
};

export const BulkFocusAskCreateAction = (
	props: BulkFocusAskCreateActionProps,
): React.JSX.Element | null => {
	di(FocusAskCreateModal);

	const {
		actions,
		cloudId,
		orgId,
		getStatus,
		onCustomDataConfirm,
		recipientAgentId,
		conversationId,
		messageId,
		sendMessageConfirmedTools,
	} = props;
	const { formatMessage } = useIntl();

	const action = actions[0];
	const asks = useMemo(() => extractAsks(action?.data), [action]);

	const state = useBulkFocusAskState(action?.invocationId ?? '', asks);
	const { rows, getFormData, updateFormData, removedIds, removeRow, dismissAllRows } = state;

	const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);
	const [bulkOutcome, setBulkOutcome] = useState<'idle' | 'success' | 'error'>('idle');
	const [createdLinksByRowId, setCreatedLinksByRowId] = useState<Record<string, string>>({});
	const [requestErrorMessage, setRequestErrorMessage] = useState<string | null>(null);
	const [editedRowId, setEditedRowId] = useState<string | null>(null);

	const visibleRows = useMemo(
		() => rows.filter((row) => !removedIds.has(row.rowId)),
		[rows, removedIds],
	);

	const platformStatus = action ? getStatus(action) : 'idle';
	const isErrored = bulkOutcome === 'error' || platformStatus === 'errored';
	const allRowsCreated = !isErrored && (bulkOutcome === 'success' || platformStatus === 'finished');

	const rowsPendingCreation = useMemo(
		() => (allRowsCreated ? [] : visibleRows),
		[allRowsCreated, visibleRows],
	);

	const invalidRowIds = useMemo(() => {
		const invalid = new Set<string>();
		for (const row of rowsPendingCreation) {
			const formData = getFormData(row.rowId);
			const trimmedName = formData.name.trim();
			if (!trimmedName || trimmedName.length > MAX_ASK_NAME_LENGTH) {
				invalid.add(row.rowId);
			}
		}
		return invalid;
	}, [rowsPendingCreation, getFormData]);

	const getRowStatus = useCallback(
		(_rowId: string): BulkFocusAskRowStatus => {
			if (isErrored) {
				return 'errored';
			}
			if (allRowsCreated) {
				return 'created';
			}
			if (isSubmittingBulk || platformStatus === 'started') {
				return 'submitting';
			}
			return 'idle';
		},
		[allRowsCreated, isErrored, isSubmittingBulk, platformStatus],
	);

	const applyCreatedLinks = useCallback(
		(createdLinks: string[]) => {
			setCreatedLinksByRowId((current) => {
				const next = { ...current };
				rowsPendingCreation.forEach((row, index) => {
					const link = createdLinks[index];
					if (link) {
						next[row.rowId] = link;
					}
				});
				return next;
			});
		},
		[rowsPendingCreation],
	);

	const handleCreateAll = useCallback(async () => {
		if (!action || rowsPendingCreation.length === 0 || invalidRowIds.size > 0) {
			return;
		}
		setRequestErrorMessage(null);
		setBulkOutcome('idle');
		setIsSubmittingBulk(true);

		const mergedAsks = rowsPendingCreation.map((row) =>
			mergeFocusAskFormIntoPayload(row.originalAsk, getFormData(row.rowId)),
		);
		const data = { asks: mergedAsks };

		try {
			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				const idleActions = actions.filter((action) => getStatus(action) === 'idle');
				const callbackData = await sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: 'focusAskCreateAction',
					},
					confirmed_tools: {
						message_id: messageId,
						tools: idleActions.map((idleAction) =>
							idleAction.invocationId === action.invocationId
								? buildToolFromAction(idleAction, 'confirmed', data)
								: buildToolFromAction(idleAction, 'cancelled'),
						),
					},
				});
				if (isCreateAskErrorResponse(callbackData)) {
					setBulkOutcome('error');
					setRequestErrorMessage(formatMessage(messages.bulkRequestFailedMessage));
				}
				return;
			}

			const callbackData = await onCustomDataConfirm(action, data, extractCreatedAskLinks);
			if (isCreateAskErrorResponse(callbackData)) {
				setBulkOutcome('error');
				setRequestErrorMessage(formatMessage(messages.bulkRequestFailedMessage));
				return;
			}
			applyCreatedLinks(Array.isArray(callbackData) ? callbackData : []);
			setBulkOutcome('success');
		} catch (error) {
			setBulkOutcome('error');
			setRequestErrorMessage(formatMessage(messages.bulkRequestFailedMessage));
			logException({
				exception: error,
				name: 'BulkFocusAskCreateAction.onCustomDataConfirm.failed',
				tags: {
					component: 'BulkFocusAskCreateAction',
					invocationId: action.invocationId,
				},
			});
		} finally {
			setIsSubmittingBulk(false);
		}
	}, [
		action,
		rowsPendingCreation,
		invalidRowIds,
		getFormData,
		actions,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		onCustomDataConfirm,
		applyCreatedLinks,
		formatMessage,
	]);

	const showFooter =
		rowsPendingCreation.length > 0 &&
		(bulkOutcome === 'idle' || bulkOutcome === 'error' || isSubmittingBulk);
	const totalCount = rowsPendingCreation.length;
	const editedRowFormData = editedRowId ? getFormData(editedRowId) : null;
	const createAllDisabled =
		allRowsCreated ||
		isSubmittingBulk ||
		rowsPendingCreation.length === 0 ||
		invalidRowIds.size > 0 ||
		totalCount > MAX_ASKS_PER_REQUEST;

	const handleOpenEdit = useCallback((rowId: string) => {
		setEditedRowId(rowId);
	}, []);

	const handleCloseEdit = useCallback(() => {
		setEditedRowId(null);
	}, []);

	const handleEditSubmit = useCallback(
		(formData: FocusAskFormData) => {
			if (!editedRowId) {
				return;
			}
			updateFormData(editedRowId, formData);
			setEditedRowId(null);
		},
		[editedRowId, updateFormData],
	);

	const handleRemoveRow = useCallback(
		(rowId: string) => {
			removeRow(rowId);
			setCreatedLinksByRowId((current) => {
				const next = { ...current };
				delete next[rowId];
				return next;
			});
			setEditedRowId((currentRowId) => (currentRowId === rowId ? null : currentRowId));
		},
		[removeRow],
	);

	const handleDismissAll = useCallback(() => {
		dismissAllRows();
		setEditedRowId(null);
	}, [dismissAllRows]);

	const loadingSummary = isSubmittingBulk ? formatMessage(messages.bulkProgressMessage) : undefined;
	const failureSummary =
		!isSubmittingBulk && bulkOutcome === 'error'
			? formatMessage(messages.bulkFailureMessage)
			: undefined;
	const successSummary =
		bulkOutcome === 'success'
			? formatMessage(messages.bulkSuccessMessage, { count: visibleRows.length })
			: undefined;
	const validationSummary =
		invalidRowIds.size > 0
			? formatMessage(messages.bulkValidationMessage, { count: invalidRowIds.size })
			: undefined;
	const overLimitSummary =
		totalCount > MAX_ASKS_PER_REQUEST
			? formatMessage(messages.bulkOverLimitMessage, { limit: MAX_ASKS_PER_REQUEST })
			: undefined;

	if (!action || visibleRows.length === 0) {
		return null;
	}

	return (
		<FocusAskActionCardWrapper>
			<Box testId="bulk-focus-ask-rows">
				<Stack space="space.050">
					{visibleRows.map((row) => (
						<BulkFocusAskRow
							key={row.rowId}
							rowId={row.rowId}
							formData={getFormData(row.rowId)}
							status={getRowStatus(row.rowId)}
							createdLink={createdLinksByRowId[row.rowId]}
							onEdit={handleOpenEdit}
							onRemove={handleRemoveRow}
						/>
					))}
				</Stack>
			</Box>

			{showFooter && (
				<ActionCardFooter testId="bulk-focus-ask-footer">
					<Box xcss={styles.footerContent}>
						<Button
							appearance="subtle"
							onClick={handleDismissAll}
							isDisabled={isSubmittingBulk}
							testId="bulk-focus-ask-dismiss-all"
						>
							{formatMessage(messages.bulkDismissAllButton)}
						</Button>
						<Inline space="space.100" alignBlock="center">
							<Text size="small" color="color.text.subtle">
								{loadingSummary ? <Fragment>{loadingSummary}</Fragment> : null}
								{successSummary ? <Fragment>{successSummary}</Fragment> : null}
								{failureSummary ? <Fragment>{failureSummary}</Fragment> : null}
								{validationSummary ? <Fragment>{validationSummary}</Fragment> : null}
								{overLimitSummary ? <Fragment>{overLimitSummary}</Fragment> : null}
								{requestErrorMessage ? <Fragment>{requestErrorMessage}</Fragment> : null}
							</Text>
							<Button
								appearance="primary"
								onClick={handleCreateAll}
								isDisabled={createAllDisabled}
								isLoading={isSubmittingBulk}
								testId="bulk-focus-ask-create-all"
							>
								{formatMessage(messages.bulkCreateAllButton, {
									count: totalCount,
								})}
							</Button>
						</Inline>
					</Box>
				</ActionCardFooter>
			)}
			{editedRowFormData && (
				<FocusAskCreateModal
					data={editedRowFormData}
					cloudId={cloudId}
					orgId={orgId}
					onClose={handleCloseEdit}
					onSubmit={handleEditSubmit}
					title={messages.bulkEditModalTitle}
					submitLabel={messages.bulkEditModalSubmitButton}
				/>
			)}
		</FocusAskActionCardWrapper>
	);
};
